//* Brackets Components
import GameNode from "../nodes/GameNode";
import TitleNode from "../nodes/TitleNode";
import WhatIfNode from "../nodes/WhatIfNode";
import CompassNode from "../nodes/CompassNode";

//* Edges Components
import DottedSmoothStepEdge from "../edges/DottedSmoothStepEdge";

//* Define ID
export const getId = () => `bracket_${Date.now()}`;

//* Define node types
export const nodeTypes = {
  defaultNode: GameNode,
  titleNode: TitleNode,
  whatIfNode: WhatIfNode,
  compassNode: CompassNode,
};

export const edgesTypes = {
  dottedSmoothStepEdge: DottedSmoothStepEdge,
};

//* Define initial node data
export const initialNode = {
  id: getId(),
  type: "defaultNode",
  data: {
    team_1_of_type: "",
    team_1_of_type_label: "",
    team_1_of_type_value: "",
    team_1_cancelled: true,
    team_2_of_type: "",
    team_2_of_type_label: "",
    team_2_of_type_value: "",
    team_2_cancelled: true,
    game_number: 1,
  },
  position: { x: 200, y: 200 },
};

//* Default viewport
export const defaultViewport = { x: 0, y: 0, zoom: 0.6 };

//* Define the default edge option
export const defaultEdgeOptions = {
  type: "smoothstep",
  pathOptions: { offset: 5 },
};

//* Define the pro-options
export const proOptions = {
  account: "paid-pro",
  hideAttribution: true,
};

//* Generate the Nodes Data
export const generateNodesData = (
  nodes = [],
  lablesData = [],
  teamsLabel = {},
  isFront = false,
  selected = false,
  includeId = true,
  seed = false,
  activeTeamsKey = [],
  hasWorkingTeam = false
) => {
  const labels = lablesData.reduce((acc, item) => {
    acc[item.key] = item.label.replace(/\s+/g, " ").trim();
    return acc;
  }, {});

  const allLabel = { ...labels, ...teamsLabel };

  const getLabel = (type, customValue, defaultValue) => {
    if (type == "custom") {
      return customValue;
    } else {
      return type == "team"
        ? allLabel[defaultValue] || null
        : allLabel[type]
        ? `${allLabel[type]} ${defaultValue}`
        : null;
    }
  };

  const isCancelled = (type, id) => {
    if (type == "team" && activeTeamsKey.length > 0 && id > 0) {
      return activeTeamsKey.includes(String(id));
    }

    return true;
  };

  const getCustomValue = (type, customValue, defaultValue) => {
    if (type == "custom") {
      return customValue;
    } else if (defaultValue > 0) {
      return defaultValue;
    } else {
      return "";
    }
  };

  const arrNodes = nodes.map((node) => {
    let nodeData;

    const commonData = {
      type: node.type,
      height: node.height,
      width: node.width,
      selected: selected,
    };

    if (node.type == "titleNode") {
      nodeData = {
        ...commonData,
        id: node.uuid.toString(),

        data: {
          title: node.title,
          isFront,
        },
        position: JSON.parse(node.position),
        positionAbsolute: JSON.parse(node.position),
      };

      if (includeId) nodeData.title_id = node.title_id;
    } else {
      let bracketId;

      if (node.schedule_game_id) {
        bracketId = node.schedule_game_id.toString();
      } else if (node.id) {
        bracketId = node.id.toString();
      } else {
        bracketId = node.bracket_matchup_id.toString();
      }

      nodeData = {
        ...commonData,
        id: bracketId,

        position: JSON.parse(node.coordinate),
        positionAbsolute: JSON.parse(node.coordinate),
        sourcePosition: node.sourcePosition || "right",
        targetPosition: node.targetPosition || "left",
        data: {
          team_1_of_type: node.team_1_of_type,
          team_1_of_type_value: getCustomValue(
            node.team_1_of_type,
            node.team_1_of_type_custom_value,
            node.team_1_of_type_value
          ),
          team_1_of_type_label: getLabel(
            node.team_1_of_type,
            node.team_1_of_type_custom_value,
            node.team_1_of_type_value
          ),
          team_2_of_type: node.team_2_of_type,
          team_2_of_type_value: getCustomValue(
            node.team_2_of_type,
            node.team_2_of_type_custom_value,
            node.team_2_of_type_value
          ),
          team_2_of_type_label: getLabel(
            node.team_2_of_type,
            node.team_2_of_type_custom_value,
            node.team_2_of_type_value
          ),
          game_number: node.game_number,
          isFront,
          schedule_game_id: node.schedule_game_id,
          field_name: node.field_name,
          schedule_time: node.schedule_time,
          team_1_score: node.team_1_score,
          team_2_score: node.team_2_score,
          team_1_cancelled: isCancelled(
            node.team_1_of_type,
            node.team_1_of_type_value
          ),
          team_2_cancelled: isCancelled(
            node.team_2_of_type,
            node.team_2_of_type_value
          ),
          ...(hasWorkingTeam
            ? {
                hasWorkingTeam: hasWorkingTeam,
                team_3_of_type: node.work_team_type,
                team_3_of_type_label: getLabel(
                  node.work_team_type,
                  node.work_team_type_custom_value,
                  node.work_team_id
                ),
                team_3_of_type_value: getCustomValue(
                  node.work_team_type,
                  node.work_team_type_custom_value,
                  node.work_team_id
                ),
                team_3_cancelled: true,
              }
            : {}),
        },
      };

      if (isFront) {
        nodeData.data.team_1_rank = node?.teams[0]?.rank ?? 0;
        nodeData.data.team_2_rank = node?.teams[1]?.rank ?? 0;
        nodeData.data.hideSeed = seed;
      }

      if (includeId) nodeData.bracket_matchup_id = bracketId;
    }

    return nodeData;
  });

  return arrNodes.sort((a, b) => a.position.y - b.position.y);
};

export const getAllTeamsKeyVal = (data, status) => {
  let filterData = data;

  if (status > 0) {
    filterData = data.filter((item) => item.status == status);
  }

  return filterData.reduce((acc, { id, label }) => {
    acc[id] = label;
    return acc;
  }, {});
};

//* Generate the Edges Data
export const generateEdgesData = (
  edges,
  updateId = false,
  selected = false
) => {
  const arrEdges = edges.map((edge, index) => ({
    id: updateId ? `edge_${Date.now()}${index}` : edge.edge_id,
    source: edge.source.toString(),
    target: edge.target.toString(),
    type: edge.type,
    sourceHandle: edge.sourceHandle ?? null,
    targetHandle: edge.targetHandle ?? null,
    selected: selected,
  }));

  return arrEdges;
};

//* Find the missing game number
export const findFirstMissingNumber = (arr = []) => {
  if (!arr.length) return { array: [1], newNumber: 1 };

  const resultArray = arr.map((element) =>
    typeof element === "string" ? Number(element) : element
  );

  if (resultArray.length === 1) {
    const newNumber = resultArray[0] + 1;
    return { newArray: [resultArray[0], newNumber], newNumber };
  }

  let newNumber;
  resultArray.sort((a, b) => a - b);
  for (let i = 0; i < resultArray.length - 1; i++) {
    if (resultArray[i] + 1 !== resultArray[i + 1]) {
      newNumber = resultArray[i] + 1;
      resultArray.splice(i + 1, 0, newNumber);
      break;
    }
  }

  if (!newNumber) {
    newNumber = resultArray[resultArray.length - 1] + 1;
    resultArray.push(newNumber);
  }

  return { newArray: resultArray, newNumber };
};

//* Get data object
export const getGameNodesDataObj = (arr = []) => {
  return arr
    .filter((item) => item.data.game_number !== undefined)
    .map((item) => item.data || []);
};

//* Generate the dropped nodes array.
export const generateDroppedNodes = async (arr = [], isData = false) => {
  let nodesData = [];

  if (!isData) {
    nodesData = await getGameNodesDataObj(arr);
  } else {
    nodesData = arr;
  }

  const newResult = nodesData.reduce((set, item) => {
    if (item.team_1_of_type && item.team_1_of_type_value) {
      set.add(`${item.team_1_of_type}-${item.team_1_of_type_value}`);
    }

    if (item.team_2_of_type && item.team_2_of_type_value) {
      set.add(`${item.team_2_of_type}-${item.team_2_of_type_value}`);
    }

    return set;
  }, new Set());

  return [...newResult];
};

//** Remove duplicate games
export const removeDuplicateGames = (duplicateGames = {}) => {
  //* Remove if the single game number are available.
  const valueCounts = Object.values(duplicateGames).reduce(
    (acc, value) => ({ ...acc, [value]: (acc[value] || 0) + 1 }),
    {}
  );

  const removedGames = Object.fromEntries(
    Object.entries(duplicateGames).filter(
      ([_, value]) => valueCounts[value] > 1
    )
  );

  return removedGames;
};

//* Calculate the center co-ordinate when the new winner is created
export const calculateCenterCoordinate = (coord1, coord2, direction = "LR") => {
  const offset = direction == "LR" ? 290 : -290;
  const centerX = (coord1.x + coord2.x) / 2 + offset;
  const centerY = (coord1.y + coord2.y) / 2;

  return { x: centerX, y: centerY };
};

//* Calculate the helper lines
export const getHelperLines = (change, nodes, distance = 5) => {
  const defaultResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
  };
  const nodeA = nodes.find((node) => node.id === change.id);

  if (!nodeA || !change.position) {
    return defaultResult;
  }

  const nodeABounds = {
    left: change.position.x,
    right: change.position.x + (nodeA.width ?? 0),
    top: change.position.y,
    bottom: change.position.y + (nodeA.height ?? 0),
    width: nodeA.width ?? 0,
    height: nodeA.height ?? 0,
  };

  let horizontalDistance = distance;
  let verticalDistance = distance;

  return nodes
    .filter((node) => node.id !== nodeA.id)
    .reduce((result, nodeB) => {
      const nodeBBounds = {
        left: nodeB.position.x,
        right: nodeB.position.x + (nodeB.width ?? 0),
        top: nodeB.position.y,
        bottom: nodeB.position.y + (nodeB.height ?? 0),
        width: nodeB.width ?? 0,
        height: nodeB.height ?? 0,
      };

      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |
      //  |___________|
      //  |
      //  |
      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     B     |
      //  |___________|
      const distanceLeftLeft = Math.abs(nodeABounds.left - nodeBBounds.left);
      if (distanceLeftLeft < verticalDistance) {
        result.snapPosition.x = nodeBBounds.left;
        result.vertical = nodeBBounds.left;
        verticalDistance = distanceLeftLeft;
      }

      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |
      //  |___________|
      //              |
      //              |
      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     B     |
      //  |___________|
      const distanceRightRight = Math.abs(
        nodeABounds.right - nodeBBounds.right
      );

      if (distanceRightRight < verticalDistance) {
        result.snapPosition.x = nodeBBounds.right - nodeABounds.width;
        result.vertical = nodeBBounds.right;
        verticalDistance = distanceRightRight;
      }

      //              |‾‾‾‾‾‾‾‾‾‾‾|
      //              |     A     |
      //              |___________|
      //              |
      //              |
      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     B     |
      //  |___________|
      const distanceLeftRight = Math.abs(nodeABounds.left - nodeBBounds.right);

      if (distanceLeftRight < verticalDistance) {
        result.snapPosition.x = nodeBBounds.right;
        result.vertical = nodeBBounds.right;
        verticalDistance = distanceLeftRight;
      }

      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |
      //  |___________|
      //              |
      //              |
      //              |‾‾‾‾‾‾‾‾‾‾‾|
      //              |     B     |
      //              |___________|
      const distanceRightLeft = Math.abs(nodeABounds.right - nodeBBounds.left);

      if (distanceRightLeft < verticalDistance) {
        result.snapPosition.x = nodeBBounds.left - nodeABounds.width;
        result.vertical = nodeBBounds.left;
        verticalDistance = distanceRightLeft;
      }

      //  |‾‾‾‾‾‾‾‾‾‾‾|‾‾‾‾‾|‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |     |     B     |
      //  |___________|     |___________|
      const distanceTopTop = Math.abs(nodeABounds.top - nodeBBounds.top);

      if (distanceTopTop < horizontalDistance) {
        result.snapPosition.y = nodeBBounds.top;
        result.horizontal = nodeBBounds.top;
        horizontalDistance = distanceTopTop;
      }

      //  |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |
      //  |___________|_________________
      //                    |           |
      //                    |     B     |
      //                    |___________|
      const distanceBottomTop = Math.abs(nodeABounds.bottom - nodeBBounds.top);

      if (distanceBottomTop < horizontalDistance) {
        result.snapPosition.y = nodeBBounds.top - nodeABounds.height;
        result.horizontal = nodeBBounds.top;
        horizontalDistance = distanceBottomTop;
      }

      //  |‾‾‾‾‾‾‾‾‾‾‾|     |‾‾‾‾‾‾‾‾‾‾‾|
      //  |     A     |     |     B     |
      //  |___________|_____|___________|
      const distanceBottomBottom = Math.abs(
        nodeABounds.bottom - nodeBBounds.bottom
      );

      if (distanceBottomBottom < horizontalDistance) {
        result.snapPosition.y = nodeBBounds.bottom - nodeABounds.height;
        result.horizontal = nodeBBounds.bottom;
        horizontalDistance = distanceBottomBottom;
      }

      //                    |‾‾‾‾‾‾‾‾‾‾‾|
      //                    |     B     |
      //                    |           |
      //  |‾‾‾‾‾‾‾‾‾‾‾|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾
      //  |     A     |
      //  |___________|
      const distanceTopBottom = Math.abs(nodeABounds.top - nodeBBounds.bottom);

      if (distanceTopBottom < horizontalDistance) {
        result.snapPosition.y = nodeBBounds.bottom;
        result.horizontal = nodeBBounds.bottom;
        horizontalDistance = distanceTopBottom;
      }

      return result;
    }, defaultResult);
};

//* Check data for cleanup menu
export const isNodeIdIncludedOnEdges = (data, onlySource = false) => {
  if (data.nodes.length < 3 && !onlySource) return false;

  const edgeSourceNodes = data.edges.map((edge) => edge.source);
  const edgeTargetNodes = data.edges.map((edge) => edge.target);

  for (const node of data.nodes) {
    const nodeId = node.id;
    if (onlySource) {
      if (edgeSourceNodes.includes(nodeId)) {
        return false;
      }
    } else {
      if (
        !edgeSourceNodes.includes(nodeId) &&
        !edgeTargetNodes.includes(nodeId)
      ) {
        return false;
      }
    }
  }
  return true;
};

//* Selected nodes position are same
export const hasSameDirection = (nodes = []) => {
  const uniqueSourcePositions = new Set(
    // nodes.map((node) => node.sourcePosition && node.type != "whatIfNode") || []
    nodes.map((node) => node.sourcePosition) || []
  );

  const allSameSourcePosition = uniqueSourcePositions.size == 1;
  if (allSameSourcePosition) {
    const commonSourcePosition = uniqueSourcePositions.values().next().value;
    return commonSourcePosition == "right" ? "LR" : "RL";
  } else {
    return false;
  }
};

//* Update new game number
export const updateDropGameNumbers = (nodesData, highestGameNumber) => {
  let newNodeIds = { titles: {}, nodes: {} };
  let newGameNumbers = {};
  let maxGameNumber = highestGameNumber;

  const newNodes = nodesData
    .sort((a, b) => a.game_number - b.game_number)
    .map((node, index) => {
      const newNodeId = `bracket_${Date.now()}${index}`;

      if (node.type !== "titleNode") {
        const newGameNumber = maxGameNumber++;
        newGameNumbers[node.game_number] = newGameNumber;
        node.game_number = newGameNumber;

        newNodeIds.nodes[node.id] = newNodeId;
        node.id = newNodeId;
      } else {
        const newTitleNodeId = `${node.uuid}${index}`;

        node.title_id = newNodeId;
        newNodeIds.titles[node.uuid] = newTitleNodeId;
        node.uuid = newTitleNodeId;
      }

      if (node.bracket_matchup_id) {
        delete node.bracket_matchup_id;
      }

      return node;
    });

  return { newNodes, newNodeIds, newGameNumbers };
};

//* Update Game's winner/loser
export const updateWinnerLoserValue = (nodes, newGameNumbers) => {
  return nodes.map((node) => {
    const updateTeamTypeValue = (teamType) => {
      if (node[teamType] === "winner" || node[teamType] === "loser") {
        const teamValue = newGameNumbers[node[teamType + "_value"]];
        if (teamValue !== undefined) {
          node[teamType + "_value"] = teamValue;
        }
      }
    };

    updateTeamTypeValue("team_1_of_type");
    updateTeamTypeValue("team_2_of_type");

    return node;
  });
};

//* Get maximum division number from the nodes
export const getMaxValues = (nodes) => {
  return nodes.reduce(
    (result, node) => {
      if (
        node.team_1_of_type === "division" ||
        node.team_2_of_type === "division"
      ) {
        result.division = Math.max(
          result.division,
          node.team_1_of_type_value,
          node.team_2_of_type_value
        );
      }

      if (
        node.team_1_of_type === "overall" ||
        node.team_2_of_type === "overall"
      ) {
        result.overall = Math.max(
          result.overall,
          node.team_1_of_type_value,
          node.team_2_of_type_value
        );
      }

      return result;
    },
    { division: 0, overall: 0 }
  );
};

//* Move the whole layout x and y coordinates
export const moveLayout = (nodes, newPosition = { x: 0, y: 0 }) => {
  const minX = Math.min(...nodes.map((node) => node.position.x));
  const minY = Math.min(...nodes.map((node) => node.position.y));

  const offsetX = newPosition.x - minX;
  const offsetY = newPosition.y - minY;

  const newNodes = nodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x + offsetX,
      y: node.position.y + offsetY,
    },
  }));

  return newNodes;
};

export const getBracketIconPath = (img) => {
  const baseUrl = window.APP_URL;
  return `${baseUrl}images/icons/bracket-builder-v2/${img}`;
};

export const hoverToHighlight = () => {
  const inputs = document.querySelectorAll(".team-close-container input");

  // Attach a hover event listener to each input
  inputs.forEach((input) => {
    input.addEventListener("mouseover", () => {
      const hoveredValue = input.value;

      if (hoveredValue) {
        inputs.forEach((otherInput) => {
          if (otherInput.value === hoveredValue) {
            otherInput.style.setProperty(
              "background-color",
              "#f1cf47",
              "important"
            );
          } else {
            otherInput.style.backgroundColor = "";
          }
        });
      }
    });
  });

  inputs.forEach((input) => {
    input.addEventListener("mouseout", () => {
      const parentElement = input.parentElement;

      if (!input.nextElementSibling) {
        inputs.forEach((otherInput) => {
          otherInput.style.backgroundColor = "";
        });
      } else {
        if (!parentElement.classList.contains("long-team-names")) {
          inputs.forEach((otherInput) => {
            otherInput.style.backgroundColor = "";
          });
        }
      }
    });
  });
};

export const longTextHover = () => {
  var teamCloseContainers = document.querySelectorAll(".team-close-container");

  // Add event listeners to each element
  teamCloseContainers.forEach(function (element) {
    // Add event listener for mouseover (hover)
    element.addEventListener("mouseover", function () {
      element.classList.add("long-team-names");
    });

    // Add event listener for mouseout (hover out)
    element.addEventListener("mouseout", function () {
      element.classList.remove("long-team-names");
    });
  });

  var teamTextContents = document.querySelectorAll(
    ".team-close-container .textContent"
  );

  teamTextContents.forEach(function (element) {
    element.addEventListener("mouseout", function () {
      const inputs = document.querySelectorAll(".team-close-container input");
      inputs.forEach((otherInput) => {
        otherInput.style.backgroundColor = "";
      });
    });
  });
};
